<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <v-bottom-sheet :model-value="display" persistent>
    <v-card class="relative mx-auto max-w-[600px]">
      <v-card-title class="!pb-3">
        <p>{{ t('confirmation') }}</p>
      </v-card-title>

      <v-card-text>
        <div class="text-sm">{{ t('cookieConsent') }}</div>

        <div v-if="showAdditionalOptions">
          <v-form ref="formRef" v-model="valid">
            <div class="my-10 rounded-sm border p-4">
              <p class="mb-2 font-semibold">{{ t('functional') }}</p>

              <v-radio-group
                v-model:model-value="cookies1"
                :rules="rules.required"
                required
              >
                <v-radio
                  color="tertiary"
                  :value="false"
                  :label="t('disallow')"
                  disabled
                />

                <v-radio
                  color="tertiary"
                  :value="true"
                  :label="t('allow')"
                  disabled
                />
              </v-radio-group>

              <p class="text-sm">{{ t('functionalDescription') }}</p>
            </div>

            <div class="my-10 rounded-sm border p-4">
              <p class="mb-2 font-semibold">{{ t('analytics') }}</p>

              <v-radio-group
                v-model:model-value="cookies2"
                :rules="rules.required"
                required
              >
                <v-radio
                  color="tertiary"
                  :value="false"
                  :label="t('disallow')"
                />

                <v-radio color="tertiary" :value="true" :label="t('allow')" />
              </v-radio-group>

              <p class="text-sm">{{ t('analyticsDescription') }}</p>
            </div>

            <div class="my-10 rounded-sm border p-4">
              <p class="mb-2 font-semibold">{{ t('ads') }}</p>

              <v-radio-group
                v-model:model-value="cookies3"
                :rules="rules.required"
                required
              >
                <v-radio
                  color="tertiary"
                  :value="false"
                  :label="t('disallow')"
                />

                <v-radio color="tertiary" :value="true" :label="t('allow')" />
              </v-radio-group>

              <p class="text-sm">{{ t('adsDescription') }}</p>
            </div>

            <div class="my-10 rounded-sm border p-4">
              <p class="mb-2 font-semibold">{{ t('other') }}</p>

              <v-radio-group
                v-model:model-value="cookies4"
                :rules="rules.required"
                required
              >
                <v-radio
                  color="tertiary"
                  :value="false"
                  :label="t('disallow')"
                />

                <v-radio color="tertiary" :value="true" :label="t('allow')" />
              </v-radio-group>

              <p class="text-sm">{{ t('otherDescription') }}</p>
            </div>
          </v-form>
        </div>
      </v-card-text>

      <v-card-actions class="sticky bottom-0">
        <v-btn
          v-if="!showAdditionalOptions"
          color="senary"
          variant="text"
          @click="showAdditionalOptions = true"
        >
          {{ t('moreOptions') }}
        </v-btn>

        <v-spacer />

        <v-btn
          v-if="showAdditionalOptions"
          color="senary"
          variant="text"
          :disabled="!valid"
          @click="onAcceptSelections()"
        >
          {{ t('acceptSelections') }}
        </v-btn>

        <v-btn color="senary" variant="elevated" @click="emit('allow')">
          {{ t('allowAll') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-bottom-sheet>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { isSet } from '@/validators';
import { computed, ref } from 'vue';

const rules = computed(() => {
  return {
    required: [isSet(t('required'))],
  };
});

const valid = ref(false);

const cookies1 = ref(true);

const cookies2 = ref<boolean>();

const cookies3 = ref<boolean>();

const cookies4 = ref<boolean>();

const showAdditionalOptions = ref(false);

const { t } = useI18n();

withDefaults(
  defineProps<{
    display?: boolean;
    loading?: boolean;
  }>(),
  {
    display: false,
    loading: false,
  },
);
const emit = defineEmits(['allow', 'disallow']);

const onAcceptSelections = () => {
  if (cookies3.value) {
    emit('allow');

    return;
  }

  emit('disallow');
};
</script>

<style lang="scss"></style>

<i18n lang="json">
{
  "fi": {
    "acceptSelections": "Hyväksy valinnat",
    "allow": "Hyväksy",
    "disallow": "Kiellä",
    "allowAll": "Hyväksy kaikki",
    "confirmation": "Evästeet",
    "moreOptions": "Lisää",
    "cookieConsent": "Käytämme evästeitä varmistaaksemme sivuston parhaan mahdollisen käyttökokemuksen. Evästeitä käytetään mm. käyttäjän istunnon tunnistamisessa ja tilastoinnissa.",
    "ads": "Mainosevästeet",
    "adsDescription": "Mainosevästeiden avulla voidaan seurata miten eri markkinointikampanjat vaikuttavat sivuston kävijämäärään ja käyttäytymiseen. Tämä auttaa optimoimaan markkinointitoimenpiteitä ja parantamaan niiden tuottoa.",
    "analytics": "Tilastointievästeet",
    "analyticsDescription": "Tilastointievästeet auttavat meitä ymmärtämään kuinka paljon kävijöitä sivustolla on, mistä he tulevat ja mitä sivuja he katselevat. Tämä tieto on olennaista sivuston suosion ja tehokkuuden arvioimiseksi.",
    "functional": "Toiminnalliset evästeet",
    "functionalDescription": "Sivusto tarvitsee toiminnallisia evästeitä toimiakseen kunnolla. Nämä evästeet eivät kerää analytiikkatietoa käyttäjästä vaan yksilöivät käyttäjän istunnon. Nämä evästeet pitää olla käytössä jotta voit jatkaa tämän sivuston käyttöä.",
    "other": "Muut evästeet",
    "otherDescription": "Näitä evästeitä käytetään parantamaan verkkosivuston toimintaa ja havainnoimaan virhetilanteita."
  },
  "en": {
    "acceptSelections": "Accept selections",
    "allow": "Allow",
    "disallow": "Disallow",
    "allowAll": "Consent all",
    "confirmation": "Cookies",
    "moreOptions": "More",
    "cookieConsent": "We use cookies to ensure the best possible user experience on our website. We use cookies to identify your session and for statistics.",
    "ads": "Advertising cookies",
    "adsDescription": "Advertising cookies allow us to track how different marketing campaigns impact visitor numbers and behavior. This helps optimize marketing efforts and improve their returns.",
    "analytics": "Statistics cookies",
    "analyticsDescription": "Statistics cookies help us understand how many visitors our site receives, where they come from, and what pages they view. This information is essential for assessing the site's popularity and effectiveness.",
    "functional": "Functional cookies",
    "functionalDescription": "The website needs functional cookies to function properly. These cookies do not collect analytics data from you but identify your session. These cookies are required in order to continue using this site.",
    "other": "Other cookies",
    "otherDescription": "These cookies are used to improve the functionality of our website and detect errors."
  }
}
</i18n>
